import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronRight } from "lucide-react";

const TYPES = {
  resiliation: {
    label: "Résilier mon abonnement",
    emailLabel: "Résiliation de l'abonnement OPENACCESS",
    desc: "Tu arrêtes le renouvellement. Ton accès reste ouvert jusqu'à la fin de la période déjà payée.",
  },
  retractation: {
    label: "Me rétracter",
    emailLabel: "Rétractation (droit de rétractation de 14 jours)",
    desc: "Dans les 14 jours suivant ton inscription, tu peux annuler ton abonnement et demander son remboursement.",
  },
};

const inputClass =
  "w-full bg-white/[0.02] border border-white/10 rounded-sm px-4 py-3.5 !text-[15px] text-[#F5F5F0] font-light placeholder:text-[#758D85] outline-none focus:border-[#FF6B00]/60 transition-colors";

export default function ManageSubscription({ sent = false }) {
  const [step, setStep] = useState("choice");
  const [typeKey, setTypeKey] = useState(null);
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const formRef = useRef(null);

  useEffect(() => {
    document.title = sent ? "Demande envoyée — AC Investing" : "Gérer mon abonnement — AC Investing";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Résilie ton abonnement OPENACCESS ou exerce ton droit de rétractation.");
  }, [sent]);

  const type = typeKey ? TYPES[typeKey] : null;

  const goRecap = () => {
    if (formRef.current && !formRef.current.reportValidity()) return;
    setDate(
      new Date().toLocaleString("fr-FR", {
        dateStyle: "long",
        timeStyle: "short",
        timeZone: "Europe/Paris",
      })
    );
    setStep("recap");
  };

  const autoresponse = type
    ? `Bonjour ${nom},

Nous avons bien reçu ta demande le ${date} (heure de Paris).

Récapitulatif de ta demande :
- Type : ${type.emailLabel}
- Nom : ${nom}
- Email d'achat : ${email}
- Date et heure : ${date}

Ta demande sera traitée dans les meilleurs délais. Pour toute question, réponds directement à cet email ou écris à contact@acinvesting.fr.

AC Investing`
    : "";

  return (
    <main data-testid="subscription-page" className="relative pf-theme min-h-screen flex flex-col">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" data-testid="sub-nav-logo" className="font-mono text-sm tracking-[0.25em] text-[#F5F5F0]">
            AC&nbsp;INVESTING
          </Link>
          <Link
            to="/openaccess"
            data-testid="sub-nav-back"
            className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3B8B0] hover:text-[#FF6B00] transition-colors"
          >
            Retour à OPENACCESS
          </Link>
        </div>
      </header>

      <div className="flex-1 w-full max-w-3xl mx-auto px-6 py-16 md:py-24">
        {sent ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center"
            data-testid="sub-confirmation"
          >
            <span className="inline-flex w-14 h-14 rounded-full border border-[#FF6B00]/50 items-center justify-center">
              <Check size={22} strokeWidth={1.5} className="text-[#FF6B00]" />
            </span>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] mt-8">Demande envoyée.</h1>
            <p className="mt-6 !text-[15px] text-[#A3B8B0] font-light max-w-[55ch] mx-auto">
              Ta demande a bien été transmise. Tu vas recevoir un accusé de réception par email,
              avec le récapitulatif, la date et l'heure de ta demande. Pense à vérifier tes spams.
            </p>
            <p className="mt-4 !text-[15px] text-[#A3B8B0] font-light">
              Tu n'as rien reçu ? Écris à{" "}
              <a href="mailto:contact@acinvesting.fr" className="text-[#FF6B00] hover:underline underline-offset-4">
                contact@acinvesting.fr
              </a>
              .
            </p>
            <Link
              to="/"
              data-testid="sub-confirmation-home"
              className="inline-block mt-10 font-mono text-xs tracking-[0.2em] uppercase text-[#F5F5F0] border border-white/15 px-8 py-4 rounded-sm hover:border-[#FF6B00]/50 transition-colors"
            >
              Retour à l'accueil
            </Link>
          </motion.div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <span className="micro-label">Abonnement</span>
              <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] mt-6">
                Gérer mon <span className="italic text-[#FF6B00]">abonnement</span>.
              </h1>
              <p className="mt-4 !text-[15px] text-[#A3B8B0] font-light max-w-[60ch]">
                Choisis ta demande, renseigne ton nom et l'email utilisé lors de l'achat, vérifie le
                récapitulatif, puis confirme l'envoi.
              </p>
            </motion.div>

            <form ref={formRef} action="https://formsubmit.co/contact@acinvesting.fr" method="POST" className="mt-10">
              <input type="hidden" name="_subject" value={type ? `Gérer mon abonnement — ${type.emailLabel} — ${nom}` : "Gérer mon abonnement"} />
              <input type="hidden" name="_next" value="https://www.acinvesting.fr/gerer-mon-abonnement/confirmation" />
              <input type="hidden" name="_captcha" value="true" />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_autoresponse" value={autoresponse} />
              <input type="hidden" name="type_de_demande" value={type ? type.emailLabel : ""} />
              <input type="hidden" name="date_et_heure" value={date} />

              {step === "choice" && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col gap-4"
                  data-testid="sub-choice"
                >
                  {Object.entries(TYPES).map(([key, t]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setTypeKey(key);
                        setStep("form");
                      }}
                      data-testid={`sub-choice-${key}`}
                      className="group flex items-center justify-between gap-6 text-left border border-white/10 bg-white/[0.02] rounded-sm p-7 md:p-8 transition-[background-color,border-color] duration-300 hover:bg-white/[0.04] hover:border-[#D92D20]/40"
                    >
                      <span>
                        <span className="font-display text-2xl text-[#F5F5F0] block">{t.label}</span>
                        <span className="mt-2 !text-sm text-[#A3B8B0] font-light block">{t.desc}</span>
                      </span>
                      <ChevronRight
                        size={20}
                        strokeWidth={1.5}
                        className="shrink-0 text-[#758D85] transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#FF6B00]"
                      />
                    </button>
                  ))}
                </motion.div>
              )}

              <div className={step === "form" ? "block" : "hidden"} data-testid="sub-form">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                  <div className="flex items-center gap-3 mb-8">
                    <span className="micro-label !border-[#D92D20]/50 !text-[#FF6B00]">{type?.label}</span>
                    <button
                      type="button"
                      onClick={() => setStep("choice")}
                      data-testid="sub-change-type"
                      className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#758D85] hover:text-[#F5F5F0] transition-colors"
                    >
                      Changer
                    </button>
                  </div>
                  <div className="flex flex-col gap-6">
                    <div>
                      <label htmlFor="sub-nom" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3B8B0] block mb-2.5">
                        Ton nom
                      </label>
                      <input
                        id="sub-nom"
                        name="nom"
                        type="text"
                        required
                        autoComplete="name"
                        placeholder="Jean Dupont"
                        value={nom}
                        onChange={(e) => setNom(e.target.value)}
                        data-testid="sub-input-nom"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="sub-email" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3B8B0] block mb-2.5">
                        Email utilisé lors de l'achat
                      </label>
                      <input
                        id="sub-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        placeholder="jean@exemple.fr"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="sub-input-email"
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 mt-10">
                    <button
                      type="button"
                      onClick={() => setStep("choice")}
                      data-testid="sub-back-choice"
                      className="font-mono text-xs tracking-[0.2em] uppercase text-[#A3B8B0] border border-white/10 px-8 py-4 rounded-sm hover:border-white/25 transition-colors"
                    >
                      Retour
                    </button>
                    <button
                      type="button"
                      onClick={goRecap}
                      data-testid="sub-to-recap"
                      className="flex-1 bg-[#D92D20] hover:bg-[#b3241a] text-white font-mono text-xs tracking-[0.2em] uppercase py-4 px-8 rounded-sm transition-colors"
                    >
                      Voir le récapitulatif
                    </button>
                  </div>
                </motion.div>
              </div>

              {step === "recap" && type && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  data-testid="sub-recap"
                >
                  <div className="border border-white/10 bg-white/[0.02] rounded-sm">
                    <div className="px-6 py-3 border-b border-white/10">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-[#758D85]">RÉCAPITULATIF</span>
                    </div>
                    <dl className="divide-y divide-white/10">
                      {[
                        ["Type de demande", type.emailLabel],
                        ["Nom", nom],
                        ["Email d'achat", email],
                        ["Date et heure", `${date} (heure de Paris)`],
                      ].map(([k, v]) => (
                        <div key={k} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6">
                          <dt className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#758D85] sm:w-44 shrink-0">{k}</dt>
                          <dd className="!text-[15px] text-[#F5F5F0] font-light">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                  <p className="mt-6 !text-xs text-[#758D85] font-light leading-relaxed">
                    En confirmant, ta demande part vers contact@acinvesting.fr. Une vérification
                    anti-robot s'affichera, puis tu reviendras automatiquement sur une page de
                    confirmation. Tu recevras ensuite un accusé de réception par email avec ce
                    récapitulatif.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 mt-8">
                    <button
                      type="button"
                      onClick={() => setStep("form")}
                      data-testid="sub-back-form"
                      className="font-mono text-xs tracking-[0.2em] uppercase text-[#A3B8B0] border border-white/10 px-8 py-4 rounded-sm hover:border-white/25 transition-colors"
                    >
                      Modifier
                    </button>
                    <button
                      type="submit"
                      data-testid="sub-submit"
                      className="flex-1 bg-[#D92D20] hover:bg-[#b3241a] text-white font-mono text-xs tracking-[0.2em] uppercase py-4 px-8 rounded-sm transition-colors"
                    >
                      Confirmer et envoyer
                    </button>
                  </div>
                </motion.div>
              )}
            </form>
          </>
        )}
      </div>
    </main>
  );
}
